import { useEffect } from "react";

import isNull from "lodash/isNull";
import { useQuery } from "@tanstack/react-query";

import useSuperGiveAwayStore from "./store";
import { bugNotif } from "../../api/bugsnag";
import { User, UserPowerUps } from "../../../Common/models/user";
import { getRawEntryHistory } from "../../services/entries";
import {
  Giveaway,
  GiveawayWUserPastEntries,
} from "../../../Common/models/giveaways";
import { getErrorMsg } from "../../../Common/utils/common";
import {
  getOngoingGiveaways,
  getPastGiveaways,
  getUpcomingGiveaways,
} from "../../api/giveaway";
import { orderBy } from "lodash";
import { GLOBAL_QUERY_KEY, useWebUserStore } from "..";
import { calculatePastGiveawayUserEntries } from "../../../Common/utils/pastGiveawayEntries";

export const useSuperAndMysteryGiveAwayProvider = (): void => {
  const [setMajorAndSuperGiveawayList] = useSuperGiveAwayStore((state) => [
    state.setMajorAndSuperGiveawayList,
  ]);

  const { data, error } = useQuery({
    queryKey: [GLOBAL_QUERY_KEY.mysteryAndSuperGiveaways],
    queryFn: async () => {
      const resp = await getUpcomingGiveaways();
      return resp;
    },
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    if (!isNull(error)) {
      bugNotif(
        "useSuperAndMysteryGiveAwayProvider - getUpcomingGiveaways",
        getErrorMsg(error).rawErrorMsg
      );
    }
  }, [error]);

  useEffect(() => {
    // console.log(data);
    if (data !== undefined) {
      setMajorAndSuperGiveawayList(data.data.result);
    }
  }, [data]);
};

export const useSuperGiveAwayRunningGiveawayProvider = (): void => {
  const { data, error } = useQuery({
    queryKey: [GLOBAL_QUERY_KEY.ongoingGiveaway],
    queryFn: async () => {
      const resp = await getOngoingGiveaways();
      return resp;
    },
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!isNull(error)) {
      bugNotif(
        "useSuperGiveAwayRunningGiveawayProvider - getOngoingGiveaways",
        getErrorMsg(error).rawErrorMsg
      );
    }
  }, [error]);

  useEffect(() => {
    if (data !== undefined) {
      const store = useSuperGiveAwayStore.getState();
      store.setOngoingGiveaway(
        data.data.result.length > 0 ? data.data.result[0] : null
      );
    }
  }, [data]);

  // useEffectOnlyOnce(() => {
  //   return () => {
  //     const store = useSuperGiveAwayStore.getState();
  //     store.resetStore();
  //   };
  // });
};

const processGiveaways = async (
  giveAways: Giveaway[],
  user: User | null | undefined,
  powerUpsHistory: UserPowerUps[]
): Promise<GiveawayWUserPastEntries[]> => {
  let rawEntryHistory: Awaited<ReturnType<typeof getRawEntryHistory>> = [];

  const hasSignedInUser = !!user?.docId;

  if (!hasSignedInUser) {
    console.warn("User docId is undefined. Cannot fetch entry history.");
  } else {
    try {
      rawEntryHistory = await getRawEntryHistory(user.docId);
    } catch (error) {
      const { rawErrorMsg } = getErrorMsg(error);
      bugNotif(
        "usePastGiveAwaysListProvider - getRawEntryHistory",
        rawErrorMsg
      );
    }
  }

  // Never attribute another account's power-ups when there is no signed-in user
  // (logout can leave stale userPowerUpsHistory until the store reset lands).
  const safePowerUpsHistory = hasSignedInUser ? powerUpsHistory : [];
  const safeOneOffProducts = hasSignedInUser ? user?.oneOffProducts : undefined;

  return giveAways.map((giveaway) => {
    try {
      const { memberShipEntries, powerUpEntries, totalEntries } =
        calculatePastGiveawayUserEntries({
          giveaway,
          entryHistory: rawEntryHistory,
          oneOffProducts: safeOneOffProducts,
          powerUps: safePowerUpsHistory,
        });

      return {
        ...giveaway,
        pastGiveAwayEntries: {
          memberShipEntries,
          powerUpEntries,
          totalEntries,
        },
      } as GiveawayWUserPastEntries;
    } catch (error) {
      const { rawErrorMsg } = getErrorMsg(error);
      bugNotif(
        `usePastGiveAwaysListProvider - processGiveaways-${giveaway.docId}`,
        rawErrorMsg
      );
      return {
        ...giveaway,
        pastGiveAwayEntries: { memberShipEntries: 0, powerUpEntries: 0 },
      } as GiveawayWUserPastEntries;
    }
  });
};

export const useUpcomingGiveAwaysListProvider = (): void => {
  const [setUpcomingGiveawayList] = useSuperGiveAwayStore((state) => [
    state.setUpcomingGiveawayList,
  ]);

  const { data, error } = useQuery({
    queryKey: [GLOBAL_QUERY_KEY.upcomingGiveawaysList],
    queryFn: async () => {
      const resp = await getUpcomingGiveaways();
      return resp;
    },
    retry: 3,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!isNull(error)) {
      bugNotif(
        "useUpcomingGiveAwaysListProvider - getUpcomingGiveaways",
        getErrorMsg(error).errorMsg
      );
    }
  }, [error]);

  useEffect(() => {
    if (data !== undefined) {
      const giveaways = data.data.result as Giveaway[];
      setUpcomingGiveawayList(
        orderBy(giveaways, (giveaway) => giveaway.giveAwayDt, "asc")
      );
    }
  }, [data]);
};

export const usePastGiveAwaysListProvider = (): void => {
  const [setPastGiveawaysList] = useSuperGiveAwayStore((state) => [
    state.setPastGiveawaysList,
  ]);
  const user = useWebUserStore((state) => state.user);
  const userPowerUpsHistory = useWebUserStore(
    (state) => state.userPowerUpsHistory
  );

  const { data, error } = useQuery({
    queryKey: [GLOBAL_QUERY_KEY.pastGiveawaysList],
    queryFn: async () => {
      const resp = await getPastGiveaways();
      return resp;
    },
    retry: 3,
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    if (!isNull(error)) {
      bugNotif(
        "usePastGiveAwaysListProvider - getPastGiveaways",
        getErrorMsg(error).errorMsg
      );
    }
  }, [error]);

  useEffect(() => {
    if (data !== undefined) {
      const fetchGiveawaysWithEntries = async () => {
        const giveaways = data.data.result as Giveaway[];
        const giveawaysWithEntries = await processGiveaways(
          giveaways,
          user,
          userPowerUpsHistory
        );
        setPastGiveawaysList(
          orderBy(
            giveawaysWithEntries,
            (giveaway) => giveaway.giveAwayDt,
            "desc"
          )
        );
      };

      fetchGiveawaysWithEntries();
    }
  }, [data, user, userPowerUpsHistory, setPastGiveawaysList]);
};
